import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TTenantDataUI } from 'data/types/tenant.types';
import { RoutesPaths } from 'routes/Routes.types';
import { tenantApi } from '../apiSlice/tenant.slices';
import { AuthAmplify } from '../apiSlice/authAmplify.slice';
import { TUserSliceState } from '../store.types';
import {
  updateSentryUserContext,
  clearSentryUserContext,
} from 'helpers/sentryUserContext';
import { oppLocalStorage, TStorageNames } from 'helpers/storage';

const initialState: TUserSliceState = {
  isLoading: !oppLocalStorage.getItem(TStorageNames.user),
  user: oppLocalStorage.getItem(TStorageNames.user) ?? null,
  impersonateByTenant: oppLocalStorage.getItem(TStorageNames.tenantId) ?? null,
};

const userSlice = createSlice({
  name: 'userSlice',
  initialState,
  reducers: {
    setUser(state, action: PayloadAction<TTenantDataUI>) {
      state.user = action.payload;
      oppLocalStorage.setItem(TStorageNames.user, action.payload);
      updateSentryUserContext(action.payload, !!state.impersonateByTenant);
    },
    setImpersonateByTenant(state, action: PayloadAction<string | null>) {
      state.impersonateByTenant = action.payload;
      // Update Sentry context with current impersonation status
      if (state.user) {
        updateSentryUserContext(state.user, !!action.payload);
      }
    },
    // Drops the outgoing tenant's profile when another account is signing in.
    // isLoading goes back to true because a profile for the arriving account is
    // on its way: leaving it false would briefly describe a signed-out visitor
    // as an onboarding one and bounce them to /onboarding mid sign-in.
    removeUser(state) {
      state.user = null;
      state.isLoading = true;
      oppLocalStorage.removeItem(TStorageNames.user);
      clearSentryUserContext();
    },
    removeImpersonateByTenant(state) {
      window.location.replace(`/${RoutesPaths.LOGIN}`);
      state.impersonateByTenant = null;
      oppLocalStorage.clear();
      clearSentryUserContext();
    },
  },
  extraReducers: builder => {
    builder.addMatcher(
      AuthAmplify.endpoints.currentAuthUserAmplify.matchRejected,
      state => {
        state.user = null;
        oppLocalStorage.removeItem(TStorageNames.user);
        clearSentryUserContext();
      },
    );
    builder.addMatcher(
      tenantApi.endpoints.getTenantProfile.matchPending,
      state => {
        state.isLoading = true;
      },
    );
    builder.addMatcher(
      tenantApi.endpoints.getTenantProfile.matchFulfilled,
      (state, { payload }) => {
        state.user = payload;
        state.isLoading = false;
        oppLocalStorage.setItem(TStorageNames.user, payload);
        updateSentryUserContext(payload, !!state.impersonateByTenant);
      },
    );
    // Without this the arriving account's failed profile fetch settles on
    // user=null, isLoading=true, which renders as neither signed in nor
    // onboarding: no navigation, no error, and a visitor left looking at the
    // form they just submitted.
    builder.addMatcher(
      tenantApi.endpoints.getTenantProfile.matchRejected,
      state => {
        state.isLoading = false;
      },
    );
    builder.addMatcher(
      AuthAmplify.endpoints.signOutAmplify.matchFulfilled,
      state => {
        state.user = null;
        clearSentryUserContext();
      },
    );
  },
});

export default userSlice;
